'use client'

import React, { useId, useMemo, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'

import { cn } from '@/utilities/ui'

export type CraftedProjectsGridItem = {
  id: number
  title: string
  imageUrl: string
  imageAlt?: string | null
  href: string | null
  categoryIds: number[]
  categoryLabel: string | null
  cost?: string | null
  duration?: string | null
}

export type CraftedProjectsGridTab = {
  id: number
  label: string
}

type ActiveFilter = 'all' | number

/** Renders CMS heading with line breaks; legacy single-line default splits like the design. */
function headingLines(raw: string): string[] {
  const t = raw.trim()
  if (!t) return []
  if (/\r?\n/.test(t)) {
    return t
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean)
  }
  if (t === 'Beautifully Crafted Projects') {
    return ['Beautifully Crafted', 'Projects']
  }
  return [t]
}

function ProjectCardLink({ href, children }: { href: string | null; children: React.ReactNode }) {
  const stretch = 'flex h-full min-h-0 w-full min-w-0 flex-col text-inherit'
  if (!href) {
    return <div className={stretch}>{children}</div>
  }
  if (href.startsWith('/')) {
    return (
      <Link className={cn(stretch, 'no-underline')} href={href}>
        {children}
      </Link>
    )
  }
  return (
    <a
      className={cn(stretch, 'no-underline')}
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  )
}

export const CraftedProjectsGridClient: React.FC<{
  heading: string
  tabs: CraftedProjectsGridTab[]
  items: CraftedProjectsGridItem[]
  className?: string
}> = ({ heading, tabs, items, className }) => {
  const selectId = useId()
  const [active, setActive] = useState<ActiveFilter>('all')

  const visibleItems = useMemo(() => {
    if (active === 'all') return items
    return items.filter((item) => item.categoryIds.includes(active))
  }, [active, items])

  const tabBtnClass = (isOn: boolean) =>
    cn(
      'shrink-0 px-8 py-2 text-sm font-bold transition-colors text-[19px] focus-visible:relative focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#1595a7]',
      isOn ? 'bg-[#1595a7] text-white' : 'bg-white text-[#222] hover:bg-[#f0f9fa]',
    )

  const inner = (
    <>
      <div className="container-fluid ">
        <h2 className="text-[34px] font-normal leading-tight tracking-tight text-[#222]">
          {headingLines(heading).map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 ? <br /> : null}
              {line}
            </React.Fragment>
          ))}
        </h2>
        <div aria-hidden className="mt-1 h-2 w-[min(280px,75%)] bg-[#1595a7]" />
      </div>

      <div className="container-fluid mt-6 mx-auto">
        <label className="sr-only" htmlFor={selectId}>
          Filter by category
        </label>
        <div className="flex w-full justify-center mt-[4rem]">
          <div
            className="flex flex-wrap w-full border-2 border-[#009BAB] shadow-lg sm:flex-nowrap sm:w-auto sm:max-w-full sm:overflow-x-auto"
            role="tablist"
            aria-label="Project categories"
          >
            <button
              type="button"
              role="tab"
              aria-selected={active === 'all'}
              className={tabBtnClass(active === 'all')}
              onClick={() => setActive('all')}
            >
              All
            </button>
            {tabs.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active === t.id}
                className={tabBtnClass(active === t.id)}
                onClick={() => setActive(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container-fluid mt-8 ">
        {visibleItems.length === 0 ? (
          <p className="text-[17px] text-[#4b5563]">No projects in this category yet.</p>
        ) : (
          <ul className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8 [&>li]:min-h-0">
            {visibleItems.map((item) => (
              <li key={item.id} className="min-h-0">
                <ProjectCardLink href={item.href}>
                  <div className="relative aspect-[4/3] w-full overflow-hidden shadow-xl border-b-3 border-[#009BAB] bg-[#e8e8e8] sm:aspect-[700/600] min-h-[400px]! max-h-[400px]">
                    <Image
                      alt={item.imageAlt || item.title}
                      className="object-cover"
                      fill
                      sizes="(min-width: 640px) 50vw, 100vw"
                      src={item.imageUrl}
                    />
                    <div className="absolute inset-x-0 bottom-0">
                      <div className="relative flex flex-col justify-center bg-white/88 px-3 py-3 text-center">
                        {item.categoryLabel ? (
                          <span
                            className="absolute right-0 top-0 -translate-y-full bg-[#1595a7] px-4 py-[5px] text-sm font-normal text-white whitespace-nowrap"
                          >
                            {item.categoryLabel}
                          </span>
                        ) : null}
                        <span className="text-[16px] font-bold leading-snug text-[#1d1d1b] sm:text-[20px]">
                          {item.title}
                        </span>
                      </div>
                    </div>
                  </div>
                </ProjectCardLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  )

  return (
    <section className={cn('mx-auto w-full max-w-none py-8 md:py-10', className)}>{inner}</section>
  )
}
